import { throttle } from '@/utils/common';
import { MenuFoldOutlined } from '@ant-design/icons';
import { Button, Drawer, Grid, Menu } from 'antd';
import { useTheme } from 'antd-style';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppMenuItems } from './menuItems';
import { useStyle } from './styles';

type Props = {
  onSelect: () => void;
  onRequestCollapse: () => void;
  collapsed: boolean;
};
export const Sidebar = ({ onSelect: onSelectProp, collapsed, onRequestCollapse }: Props) => {
  const { styles, cx } = useStyle();
  const containerRef = useRef<HTMLDivElement>(null);
  const token = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const menuItems = useAppMenuItems();
  const { xs } = Grid.useBreakpoint();
  const onSelect = useCallback(
    ({ key }) => {
      onSelectProp();
      const fromUrl = location.pathname + location.search;
      const toUrl = `/${key.split('-').join('/')}`;
      navigate(toUrl, { viewTransition: true, replace: fromUrl === toUrl });
    },
    [onSelectProp, location]
  );

  const [openedKeys, setOpenedKeys] = useState([]);

  const selectedKeys = useMemo(() => {
    const paths = location.pathname.split('/').filter(path => !!path);
    return paths.reduce((acc, cur) => {
      const prev = acc.slice(-1, acc.length)[0];
      if (prev) {
        acc.push(`${prev}-${cur}`);
      } else {
        acc.push(cur);
      }
      return acc;
    }, []);
  }, [location]);

  const onOpenChange = useCallback(
    (openedKeys: string[]) => {
      setOpenedKeys(openedKeys);
    },
    [setOpenedKeys]
  );

  useEffect(() => {
    setOpenedKeys(keys => {
      return [].concat(keys, selectedKeys);
    });
  }, [selectedKeys]);

  useEffect(() => {
    const listener = throttle(() => {
      if (containerRef.current) {
        const scrollTop = window.scrollY;
        containerRef.current.style.height = `calc(100vh - ${Math.max(token.adminHeaderHeight - scrollTop, 0)}px)`;
      }
    }, 10);

    window.addEventListener('scroll', listener);
    return () => {
      window.removeEventListener('scroll', listener);
    };
  }, [token.adminHeaderHeight, containerRef]);

  const menu = useMemo(() => {
    return (
      <Menu
        className={styles.menu}
        // 只显示非空父节点或叶子节点
        items={menuItems}
        selectedKeys={selectedKeys}
        openKeys={openedKeys}
        mode="inline"
        onOpenChange={onOpenChange}
        onClick={onSelect}
      />
    );
  }, [menuItems, selectedKeys, openedKeys, onOpenChange, onSelect, styles.menu]);

  return (
    <>
      {xs ? (
        <Drawer
          open={!collapsed}
          onClose={onRequestCollapse}
          footer={null}
          placement="left"
          closeIcon={null}
          // classNames={{ body: styles.drawerBody, header: styles.drawerHeader }}
          styles={{ body: { padding: 0 }, header: { padding: 0, height: token.adminHeaderHeight } }}
          width={token.adminSiderWitdh}
          title={
            <div className={cx(styles.drawerTitle)}>
              <Button variant="outlined" onClick={onRequestCollapse}>
                <MenuFoldOutlined />
              </Button>
            </div>
          }>
          {menu}
        </Drawer>
      ) : (
        <div ref={containerRef} className={cx(styles.container, { collapsed })}>
          {menu}
        </div>
      )}
    </>
  );
};
